import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import Link from "@tiptap/extension-link";
import {
  createParagraphNear,
  liftEmptyBlock,
  newlineInCode,
  splitBlock,
} from "@tiptap/pm/commands";
import type { Node as PMNode } from "@tiptap/pm/model";
import type { Editor, Extensions, JSONContent } from "@tiptap/react";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useConvex } from "convex/react";
import {
  ArrowUp,
  Bold,
  Code,
  Italic,
  Link2,
  List,
  ListOrdered,
  Mic,
  Paperclip,
  Plus,
  Send,
  Square,
  SquareCode,
  Strikethrough,
  Unlink,
} from "lucide-react";
import {
  type DragEvent as ReactDragEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { AnalyticsSurface } from "@/lib/analytics/events";
import { dragEventHasFiles, filterAcceptedFiles } from "@/lib/file-drag";
import { cn } from "@/lib/utils";
import { parseFromMarkdown, serializeToMarkdown } from "../conversion";
import { DocumentEvidenceNode } from "../extensions/document-evidence-node";
import { configureMentionNode } from "../extensions/mention-node";
import { createAttachmentPasteDropExtension } from "../extensions/plugins/attachment-paste-drop";
import { MentionTokenPaste } from "../extensions/plugins/mention-token-paste";
import { SmilieReplacer } from "../extensions/smilie-replacer";
import {
  useVoiceComposer,
  type VoiceComposerPhase,
  type VoiceComposerSource,
} from "../hooks/use-voice-composer";
import { useReferencePresentationResolver } from "../mentions";
import { AttachMenu } from "./attach-menu";
import { ComposerRecordingStrip } from "./composer-recording-strip";
import { type MentionItem } from "./mention-list";
import { createMentionSuggestion, messageMentionSuggestionPluginKey } from "./mention-suggestion";
import {
  type MessageEnterKeyBehavior,
  type MessageFeatureFlags,
  type MessageToolbarPreset,
  type MessageToolbarTool,
  resolveEnterKeyBehavior,
  resolveMessageFeatureFlags,
  resolveToolbarTools,
  shouldSubmitOnEnter,
  shouldUseRealNewlineOnShiftEnter,
} from "./message-rich-text-config";
import {
  LegacyMessageRichTextLayout,
  MessageComposerLayout,
  MessageEditorRegion,
} from "./message-rich-text-layouts";
import { TranscribingIndicator } from "./transcribing-indicator";
import { useEditorMentionInteractions } from "./use-editor-mention-interactions";
import { VoiceInputButton } from "./voice-input-button";

export interface MessageRichTextSubmitPayload {
  markdown: string;
  plainText: string;
}

export interface MessageRichTextHistoryNavigation {
  entries: string[];
}

export interface MessageRichTextPrimaryAction {
  mode: "submit" | "button";
  ariaLabel: string;
  icon?: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void | Promise<void>;
}

export interface MessageRichTextCharacterLimit {
  limit: number;
  warningThreshold?: number;
}

export interface MessageRichTextVoiceInputConfig {
  projectId: Id<"projects">;
  threadId?: string | null;
  source: VoiceComposerSource;
  surface?: AnalyticsSurface;
  onTranscriptionCreated?: (transcriptionId: Id<"voiceTranscriptions">) => void;
  onPhaseChange?: (phase: VoiceComposerPhase) => void;
}

export interface MessageRichTextAttachmentsConfig {
  accept?: string;
  disabled?: boolean;
  hasReadyAttachments?: boolean;
  hasPendingAttachments?: boolean;
  attachmentsStrip?: ReactNode;
  onFilesSelected?: (files: File[]) => void | Promise<void>;
  /** Subtitle shown under "Drop files to attach" in the drag-over dropzone (e.g. accepted types). */
  dropHint?: string;
  /**
   * Controlled drop-active state. When provided, the composer reflects this flag in its dropzone
   * (and expands) but does NOT own drag events — an ancestor (e.g. the whole chat area) owns the
   * drop so a file dropped anywhere over it attaches. Leave undefined to let the composer manage
   * its own drag-and-drop over the composer box only.
   */
  dropActive?: boolean;
}

export type MessageRichTextComposerLeadingAction = "attach-menu" | "placeholder" | "none";

interface MessageRichTextInputProps {
  placeholder: string;
  onSubmit: (
    payload: MessageRichTextSubmitPayload
  ) => Promise<boolean | undefined> | boolean | undefined;
  disabled?: boolean;
  isSubmitting?: boolean;
  /**
   * Closes the send action (button and Enter) while the editor stays editable — for a
   * caller-side gate the composer cannot see, such as a fresh thread still being prepared
   * for a prefilled prompt. Distinct from `disabled`, which freezes the whole editor. The
   * button must reflect this: a send that is silently dropped in `onSubmit` looks like a
   * dead click and leaves the draft in place (LAT-870 Ask Claudia handoff).
   */
  submitDisabled?: boolean;
  submitLabel?: string;
  submitAriaLabel?: string;
  showAttachmentButton?: boolean;
  submitIconOnly?: boolean;
  submitIcon?: ReactNode;
  submitButtonSize?: "icon-xs" | "icon-sm" | "icon" | "icon-lg";
  submitButtonVariant?: "default" | "outline" | "secondary" | "ghost" | "destructive" | "link";
  /**
   * When true (with `layout: "inline"`), uses the smaller clarification-style
   * controls while preserving vertical growth for multiline drafts.
   */
  compact?: boolean;
  className?: string;
  /** Seeds the editor when this component mounts. Remount with a new key to replace the draft. */
  initialMarkdown?: string;
  resetKey?: number;
  mentionProjectId?: Id<"projects"> | null;
  editorTestId?: string;
  autoFocus?: boolean;
  /**
   * Message toolbar setup.
   *
   * Examples:
   * - `{ toolbarPreset: "none" }` => no formatting buttons
   * - `{ toolbarPreset: "developer" }` => basic + code + link tools
   */
  toolbarPreset?: MessageToolbarPreset;
  toolbarTools?: MessageToolbarTool[];
  features?: Partial<MessageFeatureFlags>;
  layout?: "stacked" | "inline";
  enterKeyBehavior?: MessageEnterKeyBehavior;
  historyNavigation?: MessageRichTextHistoryNavigation;
  primaryAction?: MessageRichTextPrimaryAction;
  allowSubmitWithPrimaryAction?: boolean;
  clearOnSubmitStart?: boolean;
  characterLimit?: MessageRichTextCharacterLimit;
  voiceInput?: MessageRichTextVoiceInputConfig;
  attachments?: MessageRichTextAttachmentsConfig;
  /**
   * Redesign v2 composer ("f3oWOl" / "Rw6ZS"): square shell, a `+` attach slot on the
   * left, mic + send/stop on the right, vertical expansion (compact single row → bottom
   * action strip, latched), a 13-line cap with scroll, and an optional footer "instruction
   * zone". Opt-in so existing inline/stacked consumers are unaffected.
   */
  composerLayout?: boolean;
  /** Render the attach (`+`) menu on the left of the action area. Composer layout only. */
  attachMenu?: boolean;
  /**
   * Composer-layout leading action. Defaults preserve existing `attachMenu` behavior:
   * attach menu when `attachMenu` is true, disabled plus placeholder otherwise.
   */
  composerLeadingAction?: MessageRichTextComposerLeadingAction;
  /** Render the footer row (instruction zone left + indicator slot right). Composer layout only. */
  showFooter?: boolean;
  /** Idle/default footer instruction. Overridden by working/recording captions when those apply. */
  footerHint?: ReactNode;
  /** Right-aligned footer slot — the context-usage indicator. Composer layout only. */
  contextIndicator?: ReactNode;
  /** Hide the trailing send/stop action (e.g. a create form where a separate button submits). */
  hideSubmitAction?: boolean;
  /** Fires on every edit with the current content — for surfaces that read the draft externally. */
  onContentChange?: (payload: MessageRichTextSubmitPayload) => void;
  /** Fires when the editor loses focus — for surfaces that persist on blur. */
  onBlur?: (payload: MessageRichTextSubmitPayload) => void;
}

const DEFAULT_FOOTER_HINT = "Shift + Enter for a new line · @ to reference elements";

interface ToolbarButtonSpec {
  key: MessageToolbarTool;
  label: string;
  icon: ReactNode;
  pressed: boolean;
  disabled: boolean;
  onClick: () => void;
}

function normalizeMessageMarkdown(markdown: string): string {
  return markdown.trim();
}

function createInitialContent(markdown: string): JSONContent {
  if (!markdown.trim()) {
    return {
      type: "doc",
      content: [{ type: "paragraph" }],
    };
  }

  return {
    type: "doc",
    content: parseFromMarkdown(markdown),
  };
}

function getEditorPayload(editor: NonNullable<ReturnType<typeof useEditor>>) {
  const json = editor.getJSON();
  const content = Array.isArray(json.content) ? (json.content as JSONContent[]) : [];
  const markdown = normalizeMessageMarkdown(
    serializeToMarkdown(content, { documentEvidence: "label" })
  );
  const plainText = editor.getText().trim();

  return {
    markdown,
    plainText,
    hasContent: plainText.length > 0,
  };
}

function normalizeLinkHref(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) || trimmed.startsWith("mailto:")) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

function getSelectedLinkHref(editor: Editor | null): string {
  if (!editor) return "";
  const linkAttrs = editor.getAttributes("link");
  return typeof linkAttrs.href === "string" ? linkAttrs.href : "";
}

function canExecute(editor: Editor, command: (e: Editor) => boolean): boolean {
  try {
    return command(editor);
  } catch {
    return false;
  }
}

function isMultilineDoc(doc: PMNode): boolean {
  // Use block separators only; mention atoms should not be treated as newlines.
  return doc.textBetween(0, doc.content.size, "\n").includes("\n");
}

export function MessageRichTextInput({
  placeholder,
  onSubmit,
  disabled = false,
  isSubmitting = false,
  submitDisabled = false,
  submitLabel = "Send",
  submitAriaLabel = "Send message",
  showAttachmentButton = false,
  submitIconOnly = false,
  submitIcon,
  submitButtonSize,
  submitButtonVariant,
  compact = false,
  className,
  initialMarkdown = "",
  resetKey,
  mentionProjectId = null,
  editorTestId,
  autoFocus = false,
  toolbarPreset = "basic",
  toolbarTools,
  features,
  layout = "stacked",
  enterKeyBehavior,
  historyNavigation,
  primaryAction,
  allowSubmitWithPrimaryAction = false,
  clearOnSubmitStart = false,
  characterLimit,
  voiceInput,
  attachments,
  composerLayout = false,
  attachMenu = false,
  composerLeadingAction,
  showFooter = false,
  footerHint = DEFAULT_FOOTER_HINT,
  contextIndicator,
  hideSubmitAction = false,
  onContentChange,
  onBlur,
}: MessageRichTextInputProps) {
  const convex = useConvex();
  const editorRef = useRef<Editor | null>(null);
  const editorScrollRef = useRef<HTMLDivElement | null>(null);
  const onContentChangeRef = useRef(onContentChange);
  onContentChangeRef.current = onContentChange;
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;
  const [hasContent, setHasContent] = useState(false);
  const hasContentRef = useRef(false);
  hasContentRef.current = hasContent;
  const [markdownLength, setMarkdownLength] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  // dragenter/dragleave fire for every descendant; a depth counter tells us when the pointer has
  // genuinely left the composer (depth back to 0) versus merely crossing a child boundary.
  const dragDepthRef = useRef(0);
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);
  const [linkHrefDraft, setLinkHrefDraft] = useState("");
  const submitCallbackRef = useRef<(() => void) | null>(null);
  const historyEntriesRef = useRef<string[]>(historyNavigation?.entries ?? []);
  const historyIndexRef = useRef<number | null>(null);
  const draftBeforeHistoryRef = useRef<string>("");
  const isProgrammaticHistoryUpdateRef = useRef(false);
  const isInlineLayout = layout === "inline";
  const [initialContent] = useState(() => createInitialContent(initialMarkdown));
  const resolvedFeatures = useMemo(() => resolveMessageFeatureFlags(features), [features]);
  const resolvedToolbarTools = useMemo(
    () => resolveToolbarTools({ toolbarPreset, toolbarTools }),
    [toolbarPreset, toolbarTools]
  );
  const resolvedEnterKeyBehavior = useMemo(
    () => resolveEnterKeyBehavior(enterKeyBehavior),
    [enterKeyBehavior]
  );
  const resolvedPrimaryAction = primaryAction ?? {
    mode: "submit",
    ariaLabel: submitAriaLabel,
    icon: submitIcon,
  };
  const resolveReferencePresentation = useReferencePresentationResolver();
  const canUseSubmitAction =
    resolvedPrimaryAction.mode === "submit" || allowSubmitWithPrimaryAction;
  const hasReadyAttachments = Boolean(attachments?.hasReadyAttachments);
  const hasPendingAttachments = Boolean(attachments?.hasPendingAttachments);

  const mentionSuggestion = useMemo(() => {
    if (!resolvedFeatures.mentions) return null;

    const cache = new Map<string, MentionItem[]>();
    return createMentionSuggestion({
      resolvePresentation: resolveReferencePresentation,
      searchItems: async (query) => {
        if (!mentionProjectId) {
          return [];
        }

        const normalizedQuery = query.trim().toLowerCase();
        if (cache.has(normalizedQuery)) {
          return cache.get(normalizedQuery) ?? [];
        }

        const results = await convex.query(api.mentions.search, {
          projectId: mentionProjectId,
          query,
          limit: 10,
        });

        const mappedResults: MentionItem[] = results.map((item) => ({
          id: item.id,
          label: item.name,
          mentionType: item.type,
          subtitle: item.subtitle,
          iconKey: item.iconKey,
        }));

        cache.set(normalizedQuery, mappedResults);
        return mappedResults;
      },
    });
  }, [convex, mentionProjectId, resolveReferencePresentation, resolvedFeatures.mentions]);

  const extensions = useMemo(() => {
    const baseExtensions: Extensions = [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        horizontalRule: false,
        link: false,
        // File drops are owned by the composer's dropzone, not the editor. The drop cursor would
        // otherwise flash a text-insertion caret over the dropzone overlay while dragging files.
        dropcursor: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        defaultProtocol: "https",
      }),
      DocumentEvidenceNode,
    ];

    if (resolvedFeatures.mentions) {
      baseExtensions.push(configureMentionNode(mentionSuggestion));
      // Chat text pasted as plain text still carries its references as tokens; without this
      // the composer would drop them to bare label text (LAT-901).
      baseExtensions.push(MentionTokenPaste);
    }

    if (resolvedFeatures.emojiReplacement) {
      baseExtensions.push(SmilieReplacer);
    }

    if (attachments?.onFilesSelected) {
      baseExtensions.push(
        createAttachmentPasteDropExtension({
          disabled: attachments.disabled,
          accept: attachments.accept,
          onFilesSelected: attachments.onFilesSelected,
        })
      );
    }

    return baseExtensions;
  }, [
    attachments?.disabled,
    attachments?.accept,
    attachments?.onFilesSelected,
    mentionSuggestion,
    resolvedFeatures.emojiReplacement,
    resolvedFeatures.mentions,
  ]);

  const editor = useEditor(
    {
      immediatelyRender: false,
      extensions,
      content: initialContent,
      editorProps: {
        attributes: {
          spellcheck: "false",
          class: cn(
            "message-editor-content overflow-y-auto text-sm outline-none",
            composerLayout
              ? // 13-line cap then scroll; pinned action row never scrolls (handled in layout).
                "leading-[1.5] min-h-[21px] max-h-[273px]"
              : cn(
                  "leading-relaxed",
                  isInlineLayout
                    ? compact
                      ? "min-h-[20px] max-h-[120px]"
                      : "min-h-[24px] max-h-[132px]"
                    : "min-h-[52px] max-h-[180px]"
                )
          ),
          ...(editorTestId ? { "data-testid": editorTestId } : {}),
        },
        handleKeyDown: (view, event) => {
          const selection = view.state.selection;
          const currentMarkdown = normalizeMessageMarkdown(
            serializeToMarkdown(
              Array.isArray(view.state.doc.toJSON().content)
                ? (view.state.doc.toJSON().content as JSONContent[])
                : [],
              { documentEvidence: "label" }
            )
          );
          const activeHistoryIndex = historyIndexRef.current;
          const canNavigateHistory =
            !event.isComposing &&
            selection.empty &&
            historyEntriesRef.current.length > 0 &&
            !messageMentionSuggestionPluginKey.getState(view.state)?.active;
          const isMultilineDraft = currentMarkdown.includes("\n");

          if (canNavigateHistory && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
            if (event.key === "ArrowUp") {
              if (activeHistoryIndex === null) {
                if (isMultilineDraft) return false;
                draftBeforeHistoryRef.current = currentMarkdown;
                historyIndexRef.current = 0;
                isProgrammaticHistoryUpdateRef.current = true;
                editorRef.current?.commands.setContent(
                  createInitialContent(historyEntriesRef.current[0] ?? ""),
                  { emitUpdate: false }
                );
                editorRef.current?.commands.focus("end");
                isProgrammaticHistoryUpdateRef.current = false;
                setHasContent(true);
                event.preventDefault();
                return true;
              }

              if (activeHistoryIndex < historyEntriesRef.current.length - 1) {
                const nextIndex = activeHistoryIndex + 1;
                historyIndexRef.current = nextIndex;
                isProgrammaticHistoryUpdateRef.current = true;
                editorRef.current?.commands.setContent(
                  createInitialContent(historyEntriesRef.current[nextIndex] ?? ""),
                  { emitUpdate: false }
                );
                editorRef.current?.commands.focus("end");
                isProgrammaticHistoryUpdateRef.current = false;
                setHasContent(true);
              }

              event.preventDefault();
              return true;
            }

            if (activeHistoryIndex === null) {
              return false;
            }

            if (activeHistoryIndex !== null) {
              if (activeHistoryIndex > 0) {
                const nextIndex = activeHistoryIndex - 1;
                historyIndexRef.current = nextIndex;
                isProgrammaticHistoryUpdateRef.current = true;
                editorRef.current?.commands.setContent(
                  createInitialContent(historyEntriesRef.current[nextIndex] ?? ""),
                  { emitUpdate: false }
                );
                editorRef.current?.commands.focus("end");
                isProgrammaticHistoryUpdateRef.current = false;
                setHasContent(true);
              } else {
                historyIndexRef.current = null;
                isProgrammaticHistoryUpdateRef.current = true;
                editorRef.current?.commands.setContent(
                  createInitialContent(draftBeforeHistoryRef.current),
                  {
                    emitUpdate: false,
                  }
                );
                editorRef.current?.commands.focus("end");
                isProgrammaticHistoryUpdateRef.current = false;
                setHasContent(Boolean(draftBeforeHistoryRef.current.trim()));
                draftBeforeHistoryRef.current = "";
              }

              event.preventDefault();
              return true;
            }
          }

          if (event.key !== "Enter" || event.isComposing) return false;
          const suggestionState = messageMentionSuggestionPluginKey.getState(view.state);
          if (suggestionState?.active) return false;

          if (event.shiftKey && shouldUseRealNewlineOnShiftEnter(resolvedEnterKeyBehavior)) {
            const handled =
              newlineInCode(view.state, view.dispatch) ||
              createParagraphNear(view.state, view.dispatch) ||
              liftEmptyBlock(view.state, view.dispatch) ||
              splitBlock(view.state, view.dispatch);

            if (handled) {
              event.preventDefault();
              return true;
            }

            return false;
          }

          const shouldSubmit = shouldSubmitOnEnter({
            behavior: resolvedEnterKeyBehavior,
            hasModifier: event.metaKey || event.ctrlKey,
            shiftKey: event.shiftKey,
            isMultiline: isMultilineDoc(view.state.doc),
          });

          if (!shouldSubmit) return false;
          if (!canUseSubmitAction) return false;
          event.preventDefault();
          submitCallbackRef.current?.();
          return true;
        },
      },
      onCreate: ({ editor: createdEditor }) => {
        editorRef.current = createdEditor;
        const { hasContent: hasText } = getEditorPayload(createdEditor);
        setHasContent(hasText);
        setMarkdownLength(getEditorPayload(createdEditor).markdown.length);
      },
      onUpdate: ({ editor: updatedEditor }) => {
        editorRef.current = updatedEditor;
        const payload = getEditorPayload(updatedEditor);
        setHasContent(payload.hasContent);
        setMarkdownLength(payload.markdown.length);
        onContentChangeRef.current?.({
          markdown: payload.markdown,
          plainText: payload.plainText,
        });
        if (!isProgrammaticHistoryUpdateRef.current && historyIndexRef.current !== null) {
          historyIndexRef.current = null;
        }
      },
      onBlur: ({ editor: blurredEditor }) => {
        const payload = getEditorPayload(blurredEditor);
        onBlurRef.current?.({
          markdown: payload.markdown,
          plainText: payload.plainText,
        });
      },
    },
    [canUseSubmitAction, editorTestId, extensions, initialContent, resolvedEnterKeyBehavior]
  );

  useEditorMentionInteractions(editor, { editable: true });

  // Voice controller for the redesigned composer recording strip. Called unconditionally
  // (Rules of Hooks); its output is only consumed on the composerLayout path when voiceInput
  // is configured. Other rich-text surfaces share the same hook for their own voice UI.
  const voice = useVoiceComposer({
    editor,
    projectId: voiceInput?.projectId ?? null,
    threadId: voiceInput?.threadId ?? null,
    source: voiceInput?.source ?? "claudia-composer",
    surface: voiceInput?.surface,
    disabled: disabled || isSubmitting || !editor,
    onTranscriptionCreated: voiceInput?.onTranscriptionCreated,
  });

  useEffect(() => {
    voiceInput?.onPhaseChange?.(voice.phase);
  }, [voice.phase, voiceInput?.onPhaseChange]);

  const handleSubmit = useCallback(async () => {
    if (!editor || disabled || submitDisabled || isSubmitting || !canUseSubmitAction) return;

    const payload = getEditorPayload(editor);
    if (!payload.hasContent && !hasReadyAttachments) return;
    const contentBeforeSubmit = clearOnSubmitStart ? editor.getJSON() : null;

    if (clearOnSubmitStart) {
      editor.commands.clearContent(true);
      editor.commands.focus("end");
      setHasContent(false);
    }

    try {
      const result = await onSubmit({
        markdown: payload.markdown,
        plainText: payload.plainText,
      });

      if (result === false) {
        if (contentBeforeSubmit) {
          editor.commands.setContent(contentBeforeSubmit);
          setHasContent(payload.hasContent);
        }
        return;
      }

      if (!clearOnSubmitStart) {
        editor.commands.clearContent(true);
        editor.commands.focus("end");
        setHasContent(false);
      }
      historyIndexRef.current = null;
      draftBeforeHistoryRef.current = "";
    } catch {
      if (contentBeforeSubmit) {
        editor.commands.setContent(contentBeforeSubmit);
        setHasContent(payload.hasContent);
      }
    }
  }, [
    canUseSubmitAction,
    clearOnSubmitStart,
    disabled,
    submitDisabled,
    editor,
    hasReadyAttachments,
    isSubmitting,
    onSubmit,
  ]);

  const applyLink = useCallback(() => {
    if (!editor) return;

    const normalizedHref = normalizeLinkHref(linkHrefDraft);
    if (!normalizedHref) return;

    const selection = editor.state.selection;
    if (selection.empty) {
      const start = selection.from;
      const end = start + normalizedHref.length;

      editor
        .chain()
        .focus()
        .insertContent(normalizedHref)
        .setTextSelection({ from: start, to: end })
        .setLink({ href: normalizedHref })
        .run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: normalizedHref }).run();
    }

    setIsLinkPopoverOpen(false);
  }, [editor, linkHrefDraft]);

  const removeLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setIsLinkPopoverOpen(false);
  }, [editor]);

  useEffect(() => {
    submitCallbackRef.current = () => {
      void handleSubmit();
    };
  }, [handleSubmit]);

  useEffect(() => {
    historyEntriesRef.current = historyNavigation?.entries ?? [];
    if (historyIndexRef.current === null) return;
    if (historyEntriesRef.current.length === 0) {
      historyIndexRef.current = null;
      draftBeforeHistoryRef.current = "";
      return;
    }

    if (historyIndexRef.current >= historyEntriesRef.current.length) {
      historyIndexRef.current = historyEntriesRef.current.length - 1;
    }
  }, [historyNavigation]);

  useEffect(() => {
    if (resetKey === undefined || !editor) return;
    editor.commands.clearContent(true);
    setHasContent(false);
    setMarkdownLength(0);
    historyIndexRef.current = null;
    draftBeforeHistoryRef.current = "";
  }, [editor, resetKey]);

  useEffect(() => {
    if (!editor || !autoFocus) return;
    editor.commands.focus("end");
  }, [autoFocus, editor]);

  // Composer expansion (redesign): latch expanded when the editor wraps past one line.
  // It stays latched until the draft is empty again, avoiding height jitter on wrap/unwrap.
  useEffect(() => {
    if (!composerLayout) return;
    const node = editorScrollRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    // Above one line (incl. a taller mention chip) but below two lines, so a single-line draft
    // with a chip stays compact and only a real wrap expands the composer.
    const SINGLE_LINE_PX = 36;
    const observer = new ResizeObserver(() => {
      // Only latch expanded while the draft has content. Once empty, the wrapper still
      // measures > SINGLE_LINE_PX because expanded view adds ~20px of padding (pt-3.5 pb-1.5)
      // on top of the editor's min-h, so an unguarded observer would re-expand the cleared
      // composer and override the `!hasContent` collapse effect below. Collapsing of the empty
      // state is owned entirely by that effect.
      if (hasContentRef.current && node.scrollHeight > SINGLE_LINE_PX) {
        setIsExpanded(true);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [composerLayout]);

  useEffect(() => {
    if (composerLayout && !hasContent) {
      setIsExpanded(false);
    }
  }, [composerLayout, hasContent]);

  // State 5: recording with existing text forces the composer to expand so the text stays
  // visible above the recording strip; it then stays expanded after the transcript is appended.
  useEffect(() => {
    if (composerLayout && voice.isRecording && hasContent) {
      setIsExpanded(true);
    }
  }, [composerLayout, voice.isRecording, hasContent]);

  useEffect(() => {
    if (!isLinkPopoverOpen) return;
    setLinkHrefDraft(getSelectedLinkHref(editor));
  }, [editor, isLinkPopoverOpen]);

  const characterLimitExceeded = Boolean(characterLimit && markdownLength > characterLimit.limit);
  const characterWarningThreshold = characterLimit?.warningThreshold ?? 0.9;
  const showCharacterCounter = Boolean(
    characterLimit && markdownLength >= characterLimit.limit * characterWarningThreshold
  );
  const canSubmit =
    canUseSubmitAction &&
    !disabled &&
    !submitDisabled &&
    !isSubmitting &&
    (hasContent || hasReadyAttachments) &&
    !hasPendingAttachments &&
    !characterLimitExceeded;
  const controlsDisabled = disabled || isSubmitting || !editor;
  const canRemoveLink = Boolean(editor?.isActive("link"));
  const primaryButtonDisabled = Boolean(
    resolvedPrimaryAction.disabled || resolvedPrimaryAction.loading
  );

  const toolbarButtons = useMemo(() => {
    if (!editor) return [] as ToolbarButtonSpec[];

    const toolToSpec: Record<MessageToolbarTool, ToolbarButtonSpec> = {
      bold: {
        key: "bold",
        label: "Toggle bold",
        icon: <Bold className="size-4" />,
        pressed: editor.isActive("bold"),
        disabled:
          controlsDisabled ||
          !canExecute(editor, (e) => e.can().chain().focus().toggleBold().run()),
        onClick: () => editor.chain().focus().toggleBold().run(),
      },
      italic: {
        key: "italic",
        label: "Toggle italic",
        icon: <Italic className="size-4" />,
        pressed: editor.isActive("italic"),
        disabled:
          controlsDisabled ||
          !canExecute(editor, (e) => e.can().chain().focus().toggleItalic().run()),
        onClick: () => editor.chain().focus().toggleItalic().run(),
      },
      strike: {
        key: "strike",
        label: "Toggle strikethrough",
        icon: <Strikethrough className="size-4" />,
        pressed: editor.isActive("strike"),
        disabled:
          controlsDisabled ||
          !canExecute(editor, (e) => e.can().chain().focus().toggleStrike().run()),
        onClick: () => editor.chain().focus().toggleStrike().run(),
      },
      inlineCode: {
        key: "inlineCode",
        label: "Toggle inline code",
        icon: <Code className="size-4" />,
        pressed: editor.isActive("code"),
        disabled:
          controlsDisabled ||
          !canExecute(editor, (e) => e.can().chain().focus().toggleCode().run()),
        onClick: () => editor.chain().focus().toggleCode().run(),
      },
      bulletList: {
        key: "bulletList",
        label: "Toggle bullet list",
        icon: <List className="size-4" />,
        pressed: editor.isActive("bulletList"),
        disabled:
          controlsDisabled ||
          !canExecute(editor, (e) => e.can().chain().focus().toggleBulletList().run()),
        onClick: () => editor.chain().focus().toggleBulletList().run(),
      },
      orderedList: {
        key: "orderedList",
        label: "Toggle ordered list",
        icon: <ListOrdered className="size-4" />,
        pressed: editor.isActive("orderedList"),
        disabled:
          controlsDisabled ||
          !canExecute(editor, (e) => e.can().chain().focus().toggleOrderedList().run()),
        onClick: () => editor.chain().focus().toggleOrderedList().run(),
      },
      codeBlock: {
        key: "codeBlock",
        label: "Toggle code block",
        icon: <SquareCode className="size-4" />,
        pressed: editor.isActive("codeBlock"),
        disabled:
          controlsDisabled ||
          !canExecute(editor, (e) => e.can().chain().focus().toggleCodeBlock().run()),
        onClick: () => editor.chain().focus().toggleCodeBlock().run(),
      },
      link: {
        key: "link",
        label: "Edit link",
        icon: <Link2 className="size-4" />,
        pressed: editor.isActive("link"),
        disabled: controlsDisabled,
        onClick: () => setIsLinkPopoverOpen(true),
      },
    };

    return resolvedToolbarTools.map((tool) => toolToSpec[tool]).filter(Boolean);
  }, [controlsDisabled, editor, resolvedToolbarTools]);

  const primaryActionButton =
    resolvedPrimaryAction.mode === "button" ? (
      <Button
        type="button"
        size={submitButtonSize ?? (submitIconOnly ? "icon-sm" : "sm")}
        variant={submitButtonVariant}
        aria-label={resolvedPrimaryAction.ariaLabel}
        onClick={() => {
          void resolvedPrimaryAction.onClick?.();
        }}
        disabled={primaryButtonDisabled}
      >
        {submitIconOnly ? (
          (resolvedPrimaryAction.icon ?? submitIcon ?? <Send className="size-4" />)
        ) : (
          <>
            {resolvedPrimaryAction.loading ? "Working..." : submitLabel}
            {!resolvedPrimaryAction.loading
              ? (resolvedPrimaryAction.icon ?? submitIcon ?? <Send className="ml-1 size-4" />)
              : null}
          </>
        )}
      </Button>
    ) : null;

  const submitButton = (
    <Button
      type="button"
      size={submitButtonSize ?? (submitIconOnly ? "icon-sm" : "sm")}
      variant={submitButtonVariant}
      aria-label={
        resolvedPrimaryAction.mode === "submit" ? resolvedPrimaryAction.ariaLabel : submitAriaLabel
      }
      onClick={() => void handleSubmit()}
      disabled={!canSubmit}
    >
      {submitIconOnly ? (
        ((resolvedPrimaryAction.mode === "submit" ? resolvedPrimaryAction.icon : submitIcon) ?? (
          <Send className="size-4" />
        ))
      ) : (
        <>
          {isSubmitting ? "Sending..." : submitLabel}
          {!isSubmitting
            ? ((resolvedPrimaryAction.mode === "submit"
                ? resolvedPrimaryAction.icon
                : submitIcon) ?? <Send className="ml-1 size-4" />)
            : null}
        </>
      )}
    </Button>
  );
  const actionButtons =
    primaryActionButton && allowSubmitWithPrimaryAction ? (
      <>
        {voiceInput ? (
          <VoiceInputButton
            editor={editor}
            projectId={voiceInput.projectId}
            threadId={voiceInput.threadId}
            source={voiceInput.source}
            surface={voiceInput.surface}
            disabled={controlsDisabled}
            compact={compact}
            onTranscriptionCreated={voiceInput.onTranscriptionCreated}
          />
        ) : null}
        {primaryActionButton}
        {submitButton}
      </>
    ) : (
      <>
        {voiceInput ? (
          <VoiceInputButton
            editor={editor}
            projectId={voiceInput.projectId}
            threadId={voiceInput.threadId}
            source={voiceInput.source}
            surface={voiceInput.surface}
            disabled={controlsDisabled}
            compact={compact}
            onTranscriptionCreated={voiceInput.onTranscriptionCreated}
          />
        ) : null}
        {primaryActionButton ?? submitButton}
      </>
    );

  if (composerLayout) {
    const isWorking = resolvedPrimaryAction.mode === "button";
    const isRecording = Boolean(voiceInput) && voice.isRecording;
    // State 3 vs 5: recording from an empty draft stays a single compact row; recording with
    // prior text (or an already-expanded composer) keeps the text above the recording strip.
    const recordingWithText = isRecording && (hasContent || isExpanded);
    const recordingCompact = isRecording && !recordingWithText;
    // Transcribing takes over the same slot the waveform used (the recorder has already
    // stopped, so isRecording is false here): show the "Transcribing audio" sweep in place.
    const isTranscribing = Boolean(voiceInput) && voice.isTranscribing;
    const transcribingWithText = isTranscribing && (hasContent || isExpanded);
    const transcribingCompact = isTranscribing && !transcribingWithText;
    const expandedView = isExpanded || recordingWithText || transcribingWithText;

    const resolvedLeadingAction =
      composerLeadingAction ?? (attachMenu ? "attach-menu" : "placeholder");
    const showRecordingLeadingPlaceholder = resolvedLeadingAction !== "none";
    const composerAttach =
      resolvedLeadingAction === "none" ? null : resolvedLeadingAction === "attach-menu" ? (
        <AttachMenu
          editor={editor}
          projectId={mentionProjectId}
          disabled={controlsDisabled || attachments?.disabled}
          accept={attachments?.accept}
          onFilesSelected={attachments?.onFilesSelected}
        />
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="size-7 shrink-0 p-0 text-muted-foreground"
          aria-label="Add context"
          disabled
        >
          <Plus className="size-[18px]" />
        </Button>
      );

    // The transcribing state is now represented by the full-width strip (which hides this
    // right cluster), so the mic no longer doubles as a transcription spinner.
    const micButton = voiceInput ? (
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="size-7 shrink-0 p-0 text-muted-foreground"
        aria-label="Start voice input"
        disabled={voice.unavailable || voice.isTranscribing}
        onClick={voice.start}
      >
        <Mic className="size-[18px]" />
      </Button>
    ) : null;

    // States 7/8: send is REPLACED by stop while working with an empty draft; the moment
    // there is text, send returns and the follow-up queues (stop hidden). Never greyed.
    const sendButton = (
      <Button
        type="button"
        variant="default"
        size="icon-sm"
        className="size-7 shrink-0 p-0"
        aria-label={submitAriaLabel}
        disabled={!canSubmit}
        onClick={() => void handleSubmit()}
      >
        {submitIcon ?? <ArrowUp className="size-4" />}
      </Button>
    );
    const stopButton = (
      <Button
        type="button"
        variant="default"
        size="icon-sm"
        className="size-7 shrink-0 p-0"
        aria-label={resolvedPrimaryAction.ariaLabel}
        disabled={primaryButtonDisabled}
        onClick={() => void resolvedPrimaryAction.onClick?.()}
      >
        {resolvedPrimaryAction.icon ?? <Square className="size-3.5 fill-current" />}
      </Button>
    );
    const trailingAction = hideSubmitAction
      ? null
      : hasContent || hasReadyAttachments
        ? sendButton
        : isWorking
          ? stopButton
          : null;

    const rightCluster = (
      <div className="flex shrink-0 items-center gap-1.5">
        {micButton}
        {trailingAction}
      </div>
    );

    // The editor div keeps the box as its parent in both compact and expanded modes —
    // only its className changes — so EditorContent and the mic are never remounted
    // (no lost focus mid-type, no reset recorder).
    const editorRegion = (
      <MessageEditorRegion
        editor={editor}
        hasContent={hasContent}
        placeholder={placeholder}
        scrollRef={editorScrollRef}
        className={cn("relative min-w-0", expandedView ? "px-3.5 pt-3.5 pb-1.5" : "flex-1")}
      />
    );

    // Whole-composer drag-and-drop (design state 2). When `attachments.dropActive` is provided the
    // composer is CONTROLLED: an ancestor (the chat area) owns the drop and just tells the composer
    // when to light up, so it attaches no drag handlers of its own (avoids double-drop). Otherwise
    // it self-manages drags over the composer box, owning the drop here — not the editor's
    // ProseMirror plugin — so a file dropped on the tray/padding/input attaches.
    const filesDroppable = Boolean(attachments?.onFilesSelected) && !attachments?.disabled;
    const controlledDropActive = attachments?.dropActive;
    const selfManagedDrop = filesDroppable && controlledDropActive === undefined;
    const resetDrag = () => {
      dragDepthRef.current = 0;
      setIsDraggingFiles(false);
    };
    const composerDropzone =
      controlledDropActive !== undefined || filesDroppable
        ? {
            active: controlledDropActive ?? isDraggingFiles,
            hint: attachments?.dropHint,
            ...(selfManagedDrop
              ? {
                  onDragEnter: (event: ReactDragEvent<HTMLDivElement>) => {
                    if (!dragEventHasFiles(event.dataTransfer)) return;
                    event.preventDefault();
                    dragDepthRef.current += 1;
                    setIsDraggingFiles(true);
                  },
                  onDragOver: (event: ReactDragEvent<HTMLDivElement>) => {
                    if (!dragEventHasFiles(event.dataTransfer)) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "copy";
                  },
                  onDragLeave: (event: ReactDragEvent<HTMLDivElement>) => {
                    if (!dragEventHasFiles(event.dataTransfer)) return;
                    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
                    if (dragDepthRef.current === 0) {
                      setIsDraggingFiles(false);
                    }
                  },
                  onDrop: (event: ReactDragEvent<HTMLDivElement>) => {
                    if (!dragEventHasFiles(event.dataTransfer)) return;
                    event.preventDefault();
                    resetDrag();
                    const files = filterAcceptedFiles(
                      Array.from(event.dataTransfer.files ?? []),
                      attachments?.accept
                    );
                    if (files.length > 0) {
                      void attachments?.onFilesSelected?.(files);
                    }
                  },
                }
              : {}),
          }
        : undefined;

    const footerCaption = isTranscribing
      ? "Transcribing your recording…"
      : isRecording
        ? voice.isPaused
          ? "Paused — start speaking and recording will continue"
          : "Listening… ✕ to discard · ✓ to transcribe into the input"
        : isWorking && hasContent
          ? "Your message will queue · clear the text to bring stop back"
          : isWorking
            ? "Claudia is working — press stop to cancel the current turn"
            : footerHint;

    return (
      <MessageComposerLayout
        className={className}
        expandedView={expandedView}
        recordingCompact={recordingCompact}
        recordingWithText={recordingWithText}
        transcribingCompact={transcribingCompact}
        transcribingWithText={transcribingWithText}
        attachSlot={composerAttach}
        editorRegion={editorRegion}
        recordingStrip={
          <ComposerRecordingStrip
            voice={voice}
            showAddContextPlaceholder={showRecordingLeadingPlaceholder}
          />
        }
        recordingStripWithText={
          <>
            <div className="mx-2.5 h-px bg-border" />
            <ComposerRecordingStrip
              voice={voice}
              className="px-2.5 pb-2.5 pt-1.5"
              showAddContextPlaceholder={showRecordingLeadingPlaceholder}
            />
          </>
        }
        transcribingStrip={
          <TranscribingIndicator showLeadingPlaceholder={showRecordingLeadingPlaceholder} />
        }
        transcribingStripWithText={
          <>
            <div className="mx-2.5 h-px bg-border" />
            <TranscribingIndicator
              className="px-2.5 pb-2.5 pt-1.5"
              showLeadingPlaceholder={showRecordingLeadingPlaceholder}
            />
          </>
        }
        rightCluster={rightCluster}
        attachmentsStrip={attachments?.attachmentsStrip}
        dropzone={composerDropzone}
        showFooter={showFooter}
        footerCaption={footerCaption}
        contextIndicator={contextIndicator}
      />
    );
  }

  const legacyToolbar =
    toolbarButtons.length > 0 ? (
      <div className="mb-2 flex items-center gap-1 border-b pb-2">
        {toolbarButtons.map((button) =>
          button.key === "link" ? (
            <Popover key="link" open={isLinkPopoverOpen} onOpenChange={setIsLinkPopoverOpen}>
              <PopoverTrigger
                aria-label={button.label}
                disabled={button.disabled}
                className={cn(
                  buttonVariants({ variant: "ghost", size: "icon-sm" }),
                  button.pressed && "bg-muted text-foreground"
                )}
              >
                {button.icon}
              </PopoverTrigger>
              <PopoverContent side="bottom" align="start" sideOffset={8} className="w-80 p-3">
                <PopoverHeader>
                  <PopoverTitle>Link</PopoverTitle>
                </PopoverHeader>
                <Input
                  value={linkHrefDraft}
                  onChange={(event) => setLinkHrefDraft(event.target.value)}
                  placeholder="https://example.com"
                  autoFocus
                />
                <div className="flex items-center justify-between gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeLink}
                    disabled={!canRemoveLink || controlsDisabled}
                  >
                    <Unlink className="mr-1 size-3.5" />
                    Remove
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={applyLink}
                    disabled={controlsDisabled || normalizeLinkHref(linkHrefDraft).length === 0}
                  >
                    Apply
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <Button
              key={button.key}
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={button.label}
              disabled={button.disabled}
              className={cn(button.pressed && "bg-muted text-foreground")}
              onClick={button.onClick}
            >
              {button.icon}
            </Button>
          )
        )}
      </div>
    ) : null;

  const characterCountLabel = characterLimit
    ? `${markdownLength.toLocaleString()} / ${characterLimit.limit.toLocaleString()}`
    : undefined;
  const legacyEditorRegion = (
    <MessageEditorRegion
      editor={editor}
      hasContent={hasContent}
      placeholder={placeholder}
      className={isInlineLayout ? "relative min-w-0 flex-1" : "relative"}
      showCharacterCounter={showCharacterCounter && Boolean(characterLimit)}
      characterCountLabel={characterCountLabel}
      characterLimitExceeded={characterLimitExceeded}
    />
  );
  const legacyAttachmentButton = showAttachmentButton ? (
    <Button
      type="button"
      variant="ghost"
      size={isInlineLayout && compact ? "icon-xs" : "icon-sm"}
      className="shrink-0"
      aria-label="Attach file"
      disabled
    >
      <Paperclip className={isInlineLayout && compact ? "size-3.5" : "size-4"} />
    </Button>
  ) : null;

  return (
    <LegacyMessageRichTextLayout
      className={className}
      compact={compact}
      isInlineLayout={isInlineLayout}
      toolbar={legacyToolbar}
      editorRegion={legacyEditorRegion}
      attachmentButton={legacyAttachmentButton}
      actionButtons={actionButtons}
    />
  );
}
